import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import * as SockJS from 'sockjs-client';
import { Client, StompSubscription, IFrame, IMessage } from '@stomp/stompjs';
import { environment } from 'src/environments/environment';

export interface WebSocketMessage {
  token: string;
  assessmentDriveSetId?: number;
}

export interface WebSocketResponse {
  status?: number | string;
  message?: string;
  disconnect?: boolean;
  disconnectReason?: string;
  [key: string]: any;
}

export interface LogMessage {
  timestamp: Date;
  content: string | object;
  type: 'info' | 'success' | 'error';
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private stompClient: Client;
  private currentSessionId: string | null = null;
  private wsSubscription: StompSubscription | null = null;

  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private sessionIdSubject = new BehaviorSubject<string | null>(null);
  private messagesSubject = new Subject<LogMessage>();
  private responseSubject = new Subject<WebSocketResponse>();

  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public sessionId$ = this.sessionIdSubject.asObservable();
  public messages$ = this.messagesSubject.asObservable();
  public responses$ = this.responseSubject.asObservable();
  baseUrl = environment.baseUrl;
  baseUrlWs = environment.baseUrlws;
  constructor() {
    this.stompClient = new Client({
      webSocketFactory: () => new SockJS(`${this.baseUrlWs}/ws/mobile-feed`),
      debug: (str: string) => {
        console.log(str);
        // Extract session ID from debug output
        if (str.includes('CONNECTED') && !this.currentSessionId) {
          const sessionMatch = str.match(/session:([^\n\r]+)/);
          if (sessionMatch) {
            this.currentSessionId = sessionMatch[1];
            this.sessionIdSubject.next(this.currentSessionId);
          }
        }
      }
    });

    this.stompClient.onConnect = (frame: IFrame) => {
      console.log('Connected frame:', frame);

      if (frame.headers && frame.headers.session) {
        this.currentSessionId = frame.headers.session;
      }

      // If still no session ID, try from SockJS _transport URL
      const socket = (this.stompClient as any).webSocket as any;
      if (!this.currentSessionId && socket && socket._transport && socket._transport.url) {
        this.currentSessionId = this.extractSessionId(socket._transport.url);
      }

      // Fallback: generate a unique ID if we can't get the real session ID
      if (!this.currentSessionId && socket && socket._transport && socket._transport.url) {
        const parts = socket._transport.url.split('/');
        this.currentSessionId = parts[parts.length - 2];
      }

      console.log('Session ID:', this.currentSessionId);
      this.sessionIdSubject.next(this.currentSessionId);
      this.connectionStatusSubject.next(true);
      localStorage.setItem('websocketSessionId', this.currentSessionId || '');
      this.addMessage('Successfully connected to WebSocket', 'success');
       let userAssessmentDriveSetId = sessionStorage.getItem('userAssessmentDriveSetId');
      // Subscribe to assessment-specific response queue
      if (userAssessmentDriveSetId) {
        const responseDestination = '/queue/response-' + userAssessmentDriveSetId;
        console.log('Subscribing to:', responseDestination);

        this.wsSubscription = this.stompClient.subscribe(responseDestination, (message: IMessage) => {
          console.log('Response received:', message.body);
          const response: WebSocketResponse = JSON.parse(message.body);

          if (response.disconnect === true) {
            console.warn('Disconnect flag received:', response.disconnectReason);
            this.addMessage('Error Response: ' + response.message, 'error');
            this.addMessage('Disconnecting: ' + response.disconnectReason, 'error');
            setTimeout(() => {
              this.disconnect();
            }, 2000);
          } else if (response.status === 200 || response.status === 'OK') {
            this.addMessage('Success Response:', 'success');
            this.addMessage(response, 'success');
          } else {
            this.addMessage('Error Response:', 'error');
            this.addMessage(response, 'error');
          }
          this.responseSubject.next(response);
        });
      } else {
        this.addMessage('Warning: No assessment drive ID available. Responses may not be received.', 'error');
      }
    };

    this.stompClient.onStompError = (frame: IFrame) => {
      console.error('Connection error:', frame);
      this.addMessage('Connection error: ' + frame.headers.message, 'error');
      this.connectionStatusSubject.next(false);
    };
  }

  connect(): void {
    if (!this.stompClient.active) {
      this.stompClient.activate();
    }
  }

  disconnect(): void {
    if (this.stompClient && this.stompClient.active) {
      this.stompClient.deactivate();
      if (this.wsSubscription) {
        this.wsSubscription.unsubscribe();
        this.wsSubscription = null;
      }
      console.log('Disconnected');
      this.addMessage('Disconnected from WebSocket', 'info');
      this.connectionStatusSubject.next(false);
      this.currentSessionId = null;
      this.sessionIdSubject.next(null);
    }
  }

  sendMessage(message: WebSocketMessage): void {
    if (this.stompClient && this.stompClient.connected) {
      console.log('Sending message to /app/info:', message);
      this.stompClient.publish({ destination: '/app/info', body: JSON.stringify(message) });
      this.addMessage('Sent message:', 'info');
      this.addMessage(message, 'info');
    } else {
      this.addMessage('Not connected to WebSocket', 'error');
    }
  }

  sendMobileFeedStatus(cleanToken: string, assessmentIdRaw?: any): void {
    const message: WebSocketMessage = {
          token: 'Bearer ' + cleanToken,
          assessmentDriveSetId: assessmentIdRaw
        }

    this.sendMessage(message);
  }

  private extractSessionId(url: string): string | null {
    const match = url.match(/\/([^\/]+)\/websocket$/);
    return match ? match[1] : null;
  }

  private addMessage(content: string | object, type: 'info' | 'success' | 'error'): void {
    const message: LogMessage = {
      timestamp: new Date(),
      content,
      type
    };
    this.messagesSubject.next(message);
  }

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  isConnected(): boolean {
    return this.connectionStatusSubject.value;
  }
}
